/* eslint-disable import/no-cycle, camelcase */
import {
  childService,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
  MS_PHASE_3_ENDPOINTS,
  onboardingService,
} from 'utils/msEndpointConstants';
import moment from 'moment';
import { compact, get, keyBy, uniq } from 'lodash';
import {
  camelToSnake,
  convertObjToCamelCase,
  convertObjToSnakeCase,
  isPrOrCitizen,
  retrieveDataMapper,
  retrieveListOrObject,
  retrieveObjectMapper,
} from 'utils';
import DatadogHandler from 'utils/datadog';
import {
  DATE_PICKER_FORMAT,
  DEFAULT_ERROR,
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_PAGE,
  SAVE_DATE_FORMAT,
} from 'utils/constants';
import { getCodeByIds, getLevelByIds } from 'services/SchoolConfigService';
import { getCentreByIds, getProgramByIds } from 'services/CentreService';
import {
  transformEmergencyContactInfo,
  transformListAllEnrolledChildDocuments,
  transformChildProfileByID,
  transformChildByID,
  transformPastChildren,
  transfromFetchAllChild,
  transformChildrenData,
  msTransformChildAssignToClassData,
} from '../utils/ms-utils/ms-phase-3/ChildUtils';
import {
  getAddressesByUserIds,
  getParentByIds,
  getRolesByUserIds,
  getUserByIds,
} from './UserService';
import {
  getECDARegistrationsByRegChildIds,
  getReqParentsByIds,
} from './RegistrationsService';
import {
  getClassTeachersByClassIds,
  getClassLevelByClassIds,
  getClassByIds,
} from './ClassService';
import { SkApolloRequest } from '../utils/apolloUtils';
import { GET_CHILD_BY_ID } from '../redux/models/reportPortfoliosModel';
import { getNextLevelsByLevelIds } from './SchoolConfigService';
import { getBankAccountsByChildIds } from './BankAccountService';
import { getTradeDebtNotificationConfigByChildId } from './FinanceService';
import { getFormParticipantsByFormIds, getFormsByFilter } from './FormService';

const fileName = 'src/services/ChildService.js';

const { CHILD } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;
const { CHILD: CHILD_P3, REGISTRATIONS } = MS_PHASE_3_ENDPOINTS;

export const getChildByIds = (childIds = []) => {
  if (childIds.length === 0) return Promise.resolve([]);

  return childService()
    .post(...CHILD.GET_CHILD_BY_IDS(childIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getDocumentTypesByBankAccountIds = (bankAccountIds = []) => {
  if (bankAccountIds.length === 0) return Promise.resolve([]);

  return childService()
    .post(...CHILD.GET_DOCUMENT_TYPES_BY_BANK_ACCOUNT_IDS(bankAccountIds))
    .then(resp => get(resp, 'data.document_types', {}))
    .catch(() => ({}));
};

export const getWithdrawalDraftItemByDraftId = draftId => {
  if (!draftId) return Promise.resolve(null);
  return childService()
    .get(CHILD_P3.getWithdrawalDraftItemByDraftId(draftId))
    .then(r => r?.data || null)
    .catch(() => null);
};

export const msSwitchMainApplicant = async (_, reqData) => {
  try {
    const resp = await childService().post(
      ...CHILD_P3.switchMainApplicant({
        fk_child: get(reqData, 'childID'),
        main_applicant_parent_id: get(reqData, 'applicantID'),
        spouse_parent_id: get(reqData, 'spouseID'),
      })
    );
    return {
      success: true,
      data: { switchSpouseToApplicant: get(resp, 'data') },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSwitchMainApplicant',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const msUpdateChildParticular = async (_, reqData) => {
  try {
    const resp = await childService().put(
      ...CHILD_P3.updateChildParticular({
        child_id: get(reqData, 'childID'),
        enrollment_date: get(reqData, 'enrollmentDate'),
        level_id: get(reqData, 'levelId'),
        program_type: get(reqData, 'programType'),
        ...(get(reqData, 'bypassExceeding') && {
          bypass_exceeding: get(reqData, 'bypassExceeding'),
        }),
        child_update: {
          birth_certificate: get(reqData, 'childUpdate.birthCertificate', ''),
          fk_code_kidstart_funded_by: get(
            reqData,
            'childUpdate.fkCodeKidstartFundedBy',
            ''
          ),
          gender: get(reqData, 'childUpdate.gender', ''),
          lastname: get(reqData, 'childUpdate.lastname', ''),
          nationality: get(reqData, 'childUpdate.nationality', ''),
          race: get(reqData, 'childUpdate.race', ''),
        },
      })
    );
    return {
      success: true,
      data: {
        updateChildParticular: {
          child: retrieveObjectMapper(get(resp, 'data.child'), ['ID:id']),
          childLevel: {
            level:
              retrieveObjectMapper(get(resp, 'data.child_level'), [
                'ID:fk_level',
              ]) || null,
          },
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildParticular',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
          extenstions: {
            errorCode: get(error, 'response.data.errCode'),
            ExceedingCapacity: get(error, 'response.data.exceeding_capacity'), // TODO: double check from API response when ExceedingCapacity is available
          },
        },
      ],
    };
  }
};

export const getDocumentTagsByDocTypeIds = (documentTypeConfigIds = []) => {
  if (documentTypeConfigIds.length === 0) return {};
  return childService()
    .post(
      ...CHILD_P3.getDocumentTagsByDocumentTypeConfigIds(documentTypeConfigIds)
    )
    .then(resp => get(resp, 'data.document_tags', {}))
    .catch(() => ({}));
};

export const getDocumentByIds = (documentIds = []) => {
  if (documentIds.length === 0) return Promise.resolve({});
  return childService()
    .post(...CHILD_P3.getDocumentsByIds(documentIds))
    .then(r => get(r, 'data.documents', {}))
    .catch(() => ({}));
};

export const getTransferDraftItems = (filter = {}) => {
  const filterParams = {};
  if (filter.draftIds) {
    filterParams.fk_draft = filter.draftIds;
  }
  if (filter.childIds) {
    filterParams.fk_child = filter.childIds;
  }
  return childService()
    .post(CHILD_P3.getTransferDraftItems, filterParams)
    .then(retrieveListOrObject)
    .catch(() => ({}));
};

export const getWithdrawalDraftItems = (filter = {}) => {
  const filterParams = {};
  if (filter.draftIds) {
    filterParams.fk_draft = filter.draftIds;
  }
  if (filter.childIds) {
    filterParams.fk_child = filter.childIds;
  }
  return childService()
    .post(CHILD_P3.getWithdrawalDraftItems, filterParams)
    .then(retrieveListOrObject)
    .catch(() => ({}));
};

export const getWithdrawalDraftActivityLogs = (draftIds = []) => {
  if (draftIds.length === 0) return Promise.resolve([]);

  return childService()
    .post(CHILD_P3.getWithdrawalDraftActivityLogs, {
      withdrawal_draft_ids: draftIds,
    })
    .then(r => get(r, 'data.data', []))
    .catch(error => {
      DatadogHandler.addError(error);
      DatadogHandler.sendLog(
        error,
        {
          functionName: 'getWithdrawalDraftActivityLogs',
          fileName,
        },
        'error'
      );
      return [];
    });
};

export const msFetchEmergencyContactInfo = async (_, reqData) => {
  try {
    const resp = await childService().get(
      CHILD_P3.getEmergencyContact(get(reqData, 'childID'))
    );
    const transformedData = transformEmergencyContactInfo(
      get(resp, 'data', {})
    );
    return {
      data: {
        getEmergencyContact: transformedData,
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchEmergencyContactInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchChildHealthInfo = async (_, reqData) => {
  try {
    if (!reqData?.childId) {
      return {
        success: true,
        data: { getChildHealthInfoByChildID: {} },
      };
    }
    const childSvc = childService();
    const resp = await childSvc.post(
      ...CHILD_P3.getChildHealthByChildId([reqData?.childId])
    );
    const childHealth = resp?.data?.data?.[reqData?.childId] || {};
    return {
      success: true,
      data: {
        getChildHealthInfoByChildID: {
          immunizations: childHealth?.immunizations,
          familyDoctorDetails: childHealth?.familyDoctorDetails,
          physicalConditions: childHealth?.physicalConditions,
          nonFoodAllergies: childHealth?.nonFoodAllergies,
          foodAllergies: childHealth?.foodAllergies,
          specialNeeds: childHealth?.specialNeeds,
          updatedAt: childHealth?.updatedAt,
          userByUpdatedBy: childHealth?.userByUpdatedBy && {
            ...retrieveObjectMapper(childHealth?.userByUpdatedBy, [
              'ID:id',
              'firstname',
              'lastname',
            ]),
          },
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchChildHealthInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: { message: error?.response?.data?.error || DEFAULT_ERROR },
    };
  }
};

export const msGetMeasurementsForChildByFromAndToDate = async (_, reqData) => {
  try {
    const resp = await childService().post(
      ...CHILD_P3.getMeasurementsForChildByFromAndToDate({
        child_id: get(reqData, 'childId'),
        from: get(reqData, 'from'),
        to: get(reqData, 'to'),
      })
    );
    const measurements = get(resp, 'data.data', {});
    return {
      success: true,
      data: {
        getMeasurementsForChildByFromAndToDate: {
          height: get(measurements, 'height', []),
          weight: get(measurements, 'weight', []),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetMeasurementsForChildByFromAndToDate',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const findAllEnquiry = ({ filter }) => {
  return childService()
    .post(CHILD_P3.findAllEnquiry, {
      filter: {
        active: [{ eq: true }],
      },
      child_id: [{ eq: filter.fkChild }],
    })
    .then(resp => {
      return {
        data: {
          findAllEnquiry: {
            totalCount: resp?.data?.totalCount || 0,
            data:
              resp?.data?.data?.map(item => ({
                ...retrieveObjectMapper(item, [
                  'ID:id',
                  'fk_user',
                  'fk_child',
                  'child_firstname',
                  'child_lastname',
                  'firstname',
                  'lastname',
                ]),
              })) || [],
          },
          success: true,
        },
      };
    })
    .catch(error => {
      DatadogHandler.addError(error);
      DatadogHandler.sendLog(
        error,
        {
          functionName: 'msFetchChildHealthInfo',
          fileName,
        },
        'error'
      );
      return {
        success: false,
        error: { message: error?.response?.data?.error || DEFAULT_ERROR },
      };
    });
};

export const addOrUpdateEmergencyContact = ({ childId, body }) => {
  return childService()
    .post(CHILD_P3.addOrUpdateEmergencyContact(childId), body)
    .then(() => {
      return {
        success: true,
      };
    })
    .catch(error => {
      DatadogHandler.addError(error);
      DatadogHandler.sendLog(
        error,
        {
          functionName: 'msFetchChildHealthInfo',
          fileName,
        },
        'error'
      );
      return {
        success: false,
        error: { message: error?.response?.data?.error || DEFAULT_ERROR },
      };
    });
};

export const msAddChildActivityLog = async (_, reqData) => {
  try {
    const resp = await childService().post(CHILD_P3.addChildActivityLog, {
      action: reqData.action,
      child_id: reqData.IDChild,
      mode: reqData.mode,
      purpose: reqData.purpose,
      remarks: reqData.remarks,
    });
    return {
      data: {
        addChildActivityLog: {
          ID: get(resp, 'data.id'),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'addChildActivityLog',
        fileName,
      },
      'error'
    );
    return {
      error: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
        },
      ],
    };
  }
};

export const getChildRelationsByUserIds = (userIds = []) => {
  if (userIds.length === 0) {
    return [];
  }
  return childService()
    .post(
      CHILD_P3.childRelationsByUserIds,
      {
        user_ids: userIds,
      },
      {
        params: {
          page: 1,
          perPage: userIds.length,
        },
      }
    )
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getChildClassesByChildIds = ({
  childIds = [],
  params,
  filter,
} = {}) => {
  if (childIds.length === 0) {
    return [];
  }
  return childService()
    .post(...CHILD.GET_CHILD_CLASSES_BY_CHILD_IDS(childIds, params, filter))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getChildEnrolmentStatusByChildIds = (childIds = []) => {
  if (childIds.length === 0) {
    return [];
  }
  return childService()
    .post(...CHILD_P3.getEnrolmentStatusByChildIds(childIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getChildClosestLevelByChildIds = (childIds = []) => {
  if (childIds.length === 0) {
    return [];
  }
  return childService()
    .post(...CHILD.GET_CLOSED_LEVEL_BY_CHILD_IDS(childIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getDocumentTypeByFilter = filter => {
  return childService()
    .post(CHILD_P3.getDocumentTypeByFilter, filter)
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msAssignChildrenToClass = async (_, reqBody) => {
  try {
    const resp = await childService().post(CHILD_P3.assignChildrenToClass, {
      to_class_id: get(reqBody, 'classID'),
      assign_children: (get(reqBody, 'children') || []).map(child => ({
        child_id: get(child, 'fkChild'),
        effective_date: moment
          .utc(get(child, 'effectiveDate'), SAVE_DATE_FORMAT)
          .startOf('day')
          .toISOString(),
      })),
    });
    return {
      success: true,
      data: {
        assignChildrenToClass: get(resp, 'data.assigned', false),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAssignChildrenToClass',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const msUpdateChildName = async ({ childId, name }) => {
  try {
    const res = await childService().post(CHILD_P3.updateChildName, {
      child_id: childId,
      name,
    });
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildName',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateChildBc = async ({
  childId,
  birthCertificate,
  nationality,
}) => {
  try {
    const res = await childService().post(CHILD_P3.updateChildBC, {
      child_id: childId,
      birth_certificate: birthCertificate,
      is_pr_or_citizen: isPrOrCitizen(nationality),
    });
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildBc',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateChildNationality = async ({
  childId,
  effectiveFrom,
  nationality,
}) => {
  try {
    const res = await childService().post(CHILD_P3.updateChildNationality, {
      child_id: childId,
      effective_from: effectiveFrom,
      nationality: nationality.replace('@enum:', ''),
    });
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildNationality',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateEnrolmentDateEnrolledChild = async payload => {
  try {
    const res = await childService().post(
      CHILD_P3.updateEnrolmentDateEnrolledChild,
      {
        bypass_exceeding: payload?.bypassExceeding,
        child_id: payload?.childId,
        enrolment_date: payload?.enrolmentDate,
        new_program_id: payload?.newProgramId,
        new_service_level_id: payload?.newServiceLevelId,
        program_id: payload?.programId,
        service_level_id: payload?.serviceLevelId,
      }
    );
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateEnrolmentDateEnrolledChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateChildProgramType = async payload => {
  try {
    const res = await childService().post(CHILD_P3.updateChildProgramType, {
      child_id: payload?.childId,
      effective_from_date: payload?.effectiveFromDate,
      program_id: payload?.programId,
    });
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildProgramType',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateChildGender = async payload => {
  try {
    const res = await childService().post(CHILD_P3.updateChildGender, {
      child_id: payload?.childId,
      gender: payload?.gender,
    });
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildGender',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateChildRace = async payload => {
  try {
    const res = await childService().post(CHILD_P3.updateChildRace, {
      child_id: payload?.childId,
      race_code_id: payload?.raceCode,
    });
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildRace',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateKidStartFundingForChild = async payload => {
  try {
    const res = await childService().post(
      CHILD_P3.updateKidStartFundingForChild,
      {
        child_id: payload?.childId,
        code_id: payload?.codeId,
      }
    );
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateKidStartFundingForChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msUpdateChildParticulars = async (_, { reqData }) => {
  const allApiPromises = [];
  reqData.forEach(api => {
    if (api.key === 'nationality') {
      allApiPromises.push(
        msUpdateChildNationality({
          childId: api.IDChild,
          nationality: api.nationality,
          effectiveFrom: api.effectiveFrom,
        })
      );
    }
    if (api.key === 'enrolmentDate') {
      allApiPromises.push(
        msUpdateEnrolmentDateEnrolledChild({
          childId: api.IDChild,
          enrolmentDate: api.enrolmentDate,
          programId: api.programID,
          serviceLevelId: api.serviceLevelID,
          newProgramId: api.newProgramID,
          newServiceLevelId: api.newServiceLevelID,
          bypassExceeding: api.bypassExceeding,
        })
      );
    }
    if (api.key === 'programType') {
      allApiPromises.push(
        msUpdateChildProgramType({
          childId: api.IDChild,
          effectiveFromDate: api.effectiveFrom,
          programId: api.IDProgram,
        })
      );
    }

    if (api.key === 'childName') {
      allApiPromises.push(
        msUpdateChildName({ childId: api.IDChild, name: api.fullName })
      );
    }

    if (api.key === 'childBirthCertificate') {
      allApiPromises.push(
        msUpdateChildBc({
          childId: api.IDChild,
          birthCertificate: api.birthCertificate?.trim(),
          nationality: api.nationality,
        })
      );
    }

    if (api.key === 'kidFunding') {
      allApiPromises.push(
        msUpdateKidStartFundingForChild({
          childId: api.childID,
          codeId: api.codeID,
        })
      );
    }

    if (api.key === 'childRace') {
      allApiPromises.push(
        msUpdateChildRace({
          childId: api.IDChild,
          raceCode: api.IDRaceCode,
        })
      );
    }

    if (api.key === 'childGender') {
      allApiPromises.push(
        msUpdateChildGender({
          childId: api.IDChild,
          gender: api.gender,
        })
      );
    }
  });
  try {
    const res = await Promise.all(allApiPromises);
    return {
      success: true,
      data: res,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildParticulars',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};
export const getChildCurrentClasses = (body = {}) => {
  return childService()
    .post(CHILD_P3.currentChildClasses, body)
    .then(r => get(r, 'data.result', {}))
    .catch(() => ({}));
};

export const getChildUpcomingClasses = (body = {}) => {
  return childService()
    .post(CHILD_P3.upcomingChildClasses, body)
    .then(r => get(r, 'data.result', {}))
    .catch(() => ({}));
};

export const getChildUpcomingClassesByChildIds = (body = {}) => {
  return childService()
    .post(CHILD_P3.getChildUpcomingClassesByChildIds, body)
    .then(r => get(r, 'data.result', {}))
    .catch(() => ({}));
};

export const msGetAllTransferDrafts = async (_, payload) => {
  try {
    const {
      pagination = {},
      filter,
      effectiveFrom,
      effectiveTo,
      fkChild,
    } = payload;
    const res = await childService().post(
      CHILD_P3.getTransferDrafts,
      {
        status: filter?.status?.toString()?.split(',') || null,
        additional_status:
          filter?.additionalStatus?.toString()?.split(',') || null,
        source_centre_ids: payload.sourceCentreIDs,
        dest_centre_ids: payload.destCentreIDs,
        effective_to: effectiveTo
          ? moment(effectiveTo).format(DATE_PICKER_FORMAT)
          : null,
        effective_from: effectiveFrom
          ? moment(effectiveFrom).format(DATE_PICKER_FORMAT)
          : null,
        ...(fkChild && { child_id: fkChild }),
      },
      {
        params: {
          ...(pagination?.page && { page: pagination?.page }),
          ...(pagination?.perPage && { perPage: pagination?.perPage }),
          ...(pagination?.sort && {
            sort: camelToSnake(pagination?.sort?.toString()),
          }),
        },
      }
    );
    const data = res?.data || {};
    const childIds = (data?.data || []).map(d => d?.fk_child);
    const transferDraftIds = (data?.data || []).map(d => d?.id);
    const [children, transferDraftItems] = await Promise.all([
      getChildByIds(childIds),
      getTransferDraftItems({ draftIds: transferDraftIds }),
    ]);
    const levelIds = (transferDraftItems || []).map(d => d?.fk_level);
    const programIds = (transferDraftItems || []).map(d => d?.fk_program);
    const [levels, programs] = await Promise.all([
      getLevelByIds(levelIds),
      getProgramByIds(programIds),
    ]);
    const childMap = keyBy(children, 'id');
    const transferDraftMap = keyBy(transferDraftItems, 'fk_draft');
    const levelMap = keyBy(levels, 'id');
    const programMap = keyBy(programs, 'id');

    return {
      success: true,
      data: {
        getAllTransferDrafts: {
          totalCount: data?.totalCount || 0,
          data: (data?.data || []).map(d => ({
            ...convertObjToCamelCase(d, [
              'ID:id',
              'status:status_string',
              'additionalStatus:additional_status_string',
            ]),
            child: {
              firstname: childMap[d?.fk_child]?.firstname,
              lastname: childMap[d?.fk_child]?.lastname,
              birthCertificate: childMap[d?.fk_child]?.birth_certificate,
            },
            transferDraftItem: {
              ...convertObjToCamelCase(transferDraftMap[d?.id]),
              level: {
                label: levelMap[transferDraftMap[d?.id]?.fk_level]?.label,
              },
              program: {
                label: programMap[transferDraftMap[d?.id]?.fk_program]?.label,
              },
            },
          })),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllTransferDrafts',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const getChildCurrentLevelsByChildIds = (childIds = []) => {
  if (childIds.length === 0) {
    return {};
  }
  return childService()
    .post(...CHILD_P3.getCurrentChildLevel(childIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getDocumentTypeConfigByIds = async (
  documentTypeConfigIds = []
) => {
  if (!documentTypeConfigIds?.length) return [];
  return childService()
    .get(...CHILD_P3.getDocumentTypeConfigs(documentTypeConfigIds))
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const msFetchAllEnrolledChildUploadDocs = async (_, reqData) => {
  try {
    const resp = await childService().post(
      CHILD_P3.listAllEnrolledChildDocuments,
      {
        child_id: get(reqData, 'childID'),
        document_type_config_ids: get(reqData, 'documentTypeIDs') || [],
      }
    );
    const data = get(resp, 'data.data') || [];
    const parentIds = uniq(compact(data?.map(d => d?.fk_parent)));
    const regParentIds = uniq(
      compact(data?.map(d => d?.fk_registration_parent))
    );
    const documentTypeConfigIds = uniq(
      compact(data?.map(d => d?.fk_document_type_config))
    );
    const regChildIds = uniq(compact(data?.map(d => d?.fk_registration_child)));

    const documentTags = await childService()
      .post(CHILD_P3.getDocumentTagsByFilters, {
        fk_document: [{ eq: data?.map(d => d?.id) }],
      })
      .then(retrieveListOrObject)
      .catch(() => []);

    const documentIds = uniq(compact(documentTags?.map(dt => dt?.fk_document)));
    const documents = await getDocumentByIds(documentIds);

    const userIds = uniq(
      compact([
        ...data?.map(d => d?.reviewed_by),
        ...Object.values(documents).map(d => d?.uploaded_by),
      ])
    );

    const [parents, regParents, users, documentTypeConfigs, familyMembers] =
      await Promise.all([
        getParentByIds(parentIds),
        getReqParentsByIds(regParentIds),
        getUserByIds(userIds),
        getDocumentTypeConfigByIds(documentTypeConfigIds),
        regChildIds.length > 0
          ? onboardingService()
              .post(REGISTRATIONS.getFamilyMembersByRegChildIds, {
                registration_child_ids: regChildIds,
              })
              .then(r => get(r, 'data.data') || [])
              .catch(() => [])
          : [],
      ]);

    return transformListAllEnrolledChildDocuments({
      resp,
      parents,
      regParents,
      users,
      documentTypeConfigs,
      documentTags,
      documents,
      familyMembers,
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllEnrolledChildUploadDocs',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const monoFetchChildProfileByID = async (_, reqData) => {
  try {
    const { idChild } = reqData;

    const data = await SkApolloRequest({
      params: {
        query: GET_CHILD_BY_ID,
        variables: {
          idChild,
        },
        type: 'query',
      },
    });

    return data;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'monoFetchChildProfileByID',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchChildProfileByID = async (_, reqData) => {
  try {
    const { idChild } = reqData;

    if (!idChild) {
      return {
        success: true,
        data: {
          getChildProfileByID: {},
        },
      };
    }

    const [childResp, currentClassResp] = await Promise.all([
      childService()
        .get(`/${idChild}`)
        .catch(() => ({})),
      childService()
        .post(...CHILD.GET_CHILD_CURRENT_CLASS_BY_IDS([idChild]))
        .catch(() => ({})),
    ]);

    const child = get(childResp, 'data') || {};
    const currentClass = get(currentClassResp, `data.data.${idChild}`) || {};
    const classId = currentClass?.id;

    let classTeachersData = [];
    let classTeachers = [];
    let levelData = null;

    if (classId) {
      const [classTeachersResp, classLevelResp] = await Promise.all([
        getClassTeachersByClassIds([classId]),
        getClassLevelByClassIds([classId]),
      ]);

      classTeachersData = classTeachersResp;
      levelData = classLevelResp?.[classId];

      const teacherIds = uniq(
        compact(classTeachersData?.map(ct => ct.fk_teacher))
      );
      if (teacherIds.length > 0) {
        classTeachers = await getUserByIds(teacherIds);
      }
    }

    const data = {
      getChildProfileByID: transformChildProfileByID({
        child,
        currentClass,
        classTeachersData,
        classTeachers,
        levelData,
      }),
    };

    return { success: true, data };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchChildProfileByID',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateChildHealthInfo = async (_, reqData) => {
  try {
    const res = await childService().post(
      ...CHILD_P3.updateChildHealthInfo(reqData.childId, reqData.healthInfo)
    );
    return {
      success: true,
      data: {
        updateChildHealthInfoByChildID: res?.data,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildHealthInfo',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      ...error,
    };
  }
};

export const msSaveHealthDetailsFromFile = async (_, reqData) => {
  try {
    const { classID, level, fileLoc } = reqData;
    const payload = { class_id: classID, level_id: level, file_loc: fileLoc };

    const res = await childService().post(
      ...CHILD.SAVE_HEALTH_DETAILS_FROM_FILE(payload)
    );

    return {
      success: true,
      data: {
        saveHealthDetailsFromFile: get(res, 'data.data', null),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveHealthDetailsFromFile',
        fileName,
      },
      'error'
    );
    const fullErrMsg = get(error, 'response.data.error', DEFAULT_ERROR);
    return {
      success: false,
      error: [{ message: fullErrMsg }],
    };
  }
};

export const msFetchAllActivityLogs = async payload => {
  try {
    const { filter, pagination } = payload;
    const res = await childService().post(
      CHILD_P3.findAllChildActivityLogs,
      {
        child_ids: [filter?.fkChild],
      },
      {
        params: {
          page: pagination?.page || 1,
          perPage: pagination?.perPage || 10,
          sort: pagination?.sort?.toString(),
        },
      }
    );
    const data = res?.data?.data || [];
    const purposeCodeIds = uniq((data || []).map(d => d?.fk_code_purpose));
    const userIds = uniq((data || []).map(d => d?.fk_user));
    const [users, purposeCodes] = await Promise.all([
      getUserByIds(userIds),
      getCodeByIds(purposeCodeIds),
    ]);
    const purposeCodeMap = keyBy(purposeCodes?.data, 'id');
    const userMap = keyBy(users, 'id');

    return {
      success: true,
      data: {
        findAllChildActivityLog: {
          data: data.map(d => ({
            ...convertObjToCamelCase(d),
            code: purposeCodeMap[d?.fk_code_purpose],
            user: userMap[d?.fk_user],
          })),
          totalCount: res?.data?.total_count,
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllActivityLogs',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const msSaveEnrolledChilDocument = async (_, payload) => {
  try {
    if (!payload?.childID) return { success: true, data: {} };

    const body = {
      child_id: payload?.childID,
      document_type_config_id: payload?.documentTypeConfigID,
      parent_id: payload?.parentID,
      uploaded_documents: payload?.uploadedDocuments?.map(uD => ({
        ...convertObjToCamelCase(uD, [
          'child_id:ChildID',
          'document_type_config_id:DocumentTypeConfigID',
          'document_type_id:DocumentTypeID',
          'filename:Filename',
          'key:Key',
          'parent_id:ParentID',
        ]),
      })),
    };
    const res = await childService().post(
      ...CHILD_P3.saveEnrolledChildDocument(body)
    );
    return {
      success: true,
      data: {
        saveEnrolledChildDocument: res?.data?.success || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msSaveEnrolledChilDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const getDocumentTagsByDocumentIds = async (documentIds = []) => {
  try {
    if (documentIds.length === 0) return { success: true, data: {} };

    const resp = await childService().post(
      ...CHILD_P3.getDocumentTagsByFilter({
        fk_document: [{ eq: documentIds }],
      })
    );
    return {
      success: true,
      data: resp?.data || {},
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getDocumentTagsByDocumentIds',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error,
    };
  }
};

export const msSaveDocument = async (_, reqData) => {
  try {
    if (!reqData?.IDChild) return { data: {}, success: true };

    const body = {
      documents:
        reqData?.uploadedDocuments?.map(doc => ({
          child_id: doc?.ChildID,
          ...(doc?.DocumentTypeConfigID && {
            document_type_config_id: doc?.DocumentTypeConfigID,
          }),
          ...(doc?.DocumentTypeID && {
            document_type_id: doc?.DocumentTypeID,
          }),
          ...(doc?.ParentID && {
            parent_id: doc?.ParentID,
          }),
          filename: doc?.Filename,
          key: doc?.Key,
        })) || [],
      ...(reqData?.IDEnquiry && {
        id_enquiry: reqData?.IDEnquiry,
      }),
      id_child: reqData?.IDChild,
    };
    const res = await childService().post(...CHILD_P3.saveDocument(body));
    const documentIds = uniq(compact(res?.data?.data?.map(d => d?.id) || []));
    const docTags = await getDocumentTagsByDocumentIds(documentIds);
    return {
      data: {
        saveDocument:
          res?.data?.data?.map(doc => ({
            ...convertObjToCamelCase(doc),
            documentTags: convertObjToCamelCase(docTags?.data),
          })) || [],
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msSaveDocument',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
          errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
        },
      ],
    };
  }
};

export const msGetChildPhotoUploadURL = async (_, reqData) => {
  try {
    if (!reqData?.IDChild) return { success: true, data: {} };

    const res = await childService().post(
      ...CHILD_P3.getChildPhotoUploadUrl(
        convertObjToSnakeCase(reqData, ['child_id:IDChild'])
      )
    );
    return {
      success: true,
      data: {
        getChildPhotoUploadURL:
          res?.data &&
          retrieveObjectMapper(res?.data, [
            'Url:url',
            'Key:key',
            'Filename:file_name',
          ]),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetChildPhotoUploadURL',
        fileName,
      },
      'error'
    );
    return error;
  }
};

export const msGetWithdrawalDefaultRefundAmount = async (_, reqData) => {
  try {
    const resp = await childService().post(
      CHILD_P3.getWithdrawalDefaultRefundAmount,
      {
        child_id: get(reqData, 'IDChild'),
        effective_last_date: get(reqData, 'effectiveDate'),
        is_graduation: get(reqData, 'isGraduation', false),
        is_transfer: get(reqData, 'isTransfer', false),
        submission_date: get(reqData, 'withdrawalCreatedAt'),
      }
    );

    return {
      success: true,
      data: {
        getWithdrawalDefaultRefundAmount: convertObjToCamelCase(
          get(resp, 'data', {})
        ),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetWithdrawalDefaultRefundAmount',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(
        error,
        'response.data.error',
        get(error, 'message', DEFAULT_ERROR)
      ),
    };
  }
};

export const getClosestLevelByChildIds = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return [];

    const closestLevel = await getChildClosestLevelByChildIds(childIds);
    const fkLevels = uniq(
      compact(closestLevel?.map(({ fk_level }) => fk_level) || [])
    );
    const levels = await getLevelByIds(fkLevels);
    return (
      closestLevel?.map(cL => ({
        ...cL,
        level: levels?.find(({ id }) => id === cL?.fk_level) || null,
      })) || []
    );
  } catch (error) {
    return error;
  }
};

export const getUpcomingClassByChildIds = (childIds = []) => {
  if (!childIds?.length) return {};
  return childService()
    .get(CHILD_P3.getUpcomingClassByChildIds(childIds))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getUpcomingClassesByChildIds = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return [];

    const resp = await getUpcomingClassByChildIds(childIds);
    const data = Object.values(resp?.data || {});
    const classIds = uniq(compact(data?.map(dt => dt?.fk_class) || []));
    if (classIds?.length === 0) return data;

    const classResp = await getClassByIds(classIds);
    let classes = classResp?.data || [];
    const fkLevels = uniq(compact(classes?.map(dt => dt?.fk_level) || []));
    const fkCentres = uniq(compact(classes?.map(dt => dt?.fk_centre) || []));

    const [levels, centres] = await Promise.all([
      fkLevels?.length > 0 ? getLevelByIds(fkLevels) : [],
      fkCentres?.length > 0 ? getCentreByIds(fkCentres) : {},
    ]);

    classes =
      classes?.map(cl => ({
        ...cl,
        level: levels?.find(({ id }) => id === cl?.fk_level) || null,
        centre: centres?.data?.find(({ id }) => id === cl?.fk_centre) || null,
      })) || [];

    return (
      data?.map(dt => ({
        ...dt,
        class: classes?.find(({ id }) => id === dt?.fk_class) || null,
      })) || []
    );
  } catch (error) {
    return error;
  }
};

export const msFetchPastChildrenData = async (_, { reqData }) => {
  try {
    const params = {
      page: reqData?.pagination?.page || 1,
      perPage: reqData?.pagination?.perPage || 10,
    };
    const body = {
      ...(reqData?.fkCentres?.length > 0 && {
        centre_ids: reqData?.fkCentres,
      }),
      ...(reqData?.fkChild && {
        child_id: reqData?.fkChild,
      }),
      ...(reqData?.fkLevel && {
        fk_level: reqData?.fkLevel,
      }),
      ...(reqData?.ftsInput?.length > 0 && {
        fts_input: reqData?.ftsInput,
      }),
      ...(reqData?.year && {
        year: reqData?.year,
      }),
    };
    const resp = await childService().post(
      ...CHILD_P3.getPastChildrenData(body, params)
    );
    const data = resp?.data?.data || [];
    if (data?.length === 0) {
      return {
        success: true,
        data: {
          getPastChildrenData: {
            totalCount: 0,
            data: [],
          },
        },
      };
    }

    const childIds = uniq(compact(data?.map(({ child }) => child?.id) || []));
    const [closestLevel, enrolmentStatus, upcomingClasses] = await Promise.all([
      getClosestLevelByChildIds(childIds),
      getChildEnrolmentStatusByChildIds(childIds),
      getUpcomingClassesByChildIds(childIds),
    ]);

    return {
      success: true,
      data: {
        getPastChildrenData: {
          totalCount: resp?.data?.totalCount || 0,
          data: transformPastChildren({
            closestLevel,
            enrolmentStatus,
            upcomingClasses,
            data,
          }),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchPastChildrenData',
        fileName,
      },
      'error'
    );
    return error;
  }
};

export const getChildLevelsByChildId = childId => {
  if (!childId) return [];
  return childService()
    .get(CHILD_P3.getChildLevelsByChildId, {
      child_id: childId,
    })
    .then(retrieveListOrObject)
    .catch(() => []);
};

export const getChildCurrentLevelAndCentreByChildIds = (childIds = []) => {
  if (childIds.length === 0) {
    return [];
  }
  return childService()
    .post(
      CHILD_P3.getChildCurrentLevelAndCentreByChildIds,
      { child_ids: childIds },
      { params: { page: 1, perPage: childIds.length } }
    )
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const getLastChildLevelByChildId = childId => {
  if (!childId) return {};
  return childService()
    .get(CHILD_P3.getLastChildLevelByChildId(childId))
    .then(r => r?.data)
    .catch(() => ({}));
};

export const getUpcomingLevelByChildIds = (childIds = []) => {
  if (!childIds?.length) return {};
  return childService()
    .get(CHILD_P3.getUpcomingLevelByChildIds(childIds))
    .then(r => get(r, 'data', {}))
    .catch(() => ({}));
};

export const getEnquiriesByChildId = (childId = '', filters = {}) => {
  if (!childId) return {};
  return childService()
    .post(CHILD_P3.getEnquiriesByChildId, {
      child_id: childId,
      ...filters,
    })
    .then(r => get(r, 'data', {}))
    .catch(() => ({}));
};

export const getWithdrawalDraftItemsByChildId = childId => {
  if (!childId) return {};
  return childService()
    .get(CHILD_P3.getWithdrawalDraftItemsByChildId(childId))
    .then(r => r?.data || {})
    .catch(() => ({}));
};

export const getTemporaryWithdrawalByChildId = childId => {
  if (!childId) return {};
  return childService()
    .get(CHILD_P3.getTemporaryWithdrawalByChildId(childId))
    .then(r => r?.data || {})
    .catch(() => ({}));
};

export const getChildEffectiveDateByCategory = (childId, category) => {
  if (!childId || !category) return {};
  return childService()
    .post(CHILD_P3.getChildEffectiveFrom, {
      child_id: childId,
      column: category,
    })
    .then(r => get(r, 'data.time', null))
    .catch(() => null);
};

export const msFetchChildDetails = async (_, reqData) => {
  try {
    const { IDChild } = reqData;
    const childSvc = childService();

    const [
      data,
      childLevels,
      activityLogs,
      closestLevels,
      withdrawls,
      childRelations,
      lastChildLevel,
      currentClasses,
      upcomingClasses,
      bankAccounts,
      tradeDebtNotificationConfig,
      temporaryWithdrawals,
      withdrawalDraftItems,
      childCurrentEstimatedPromotionDate,
      enrolmentDate,
      programTypeUpdateEffectiveFrom,
      nationalityUpdateEffectiveFrom,
      supportSchemaUpdateEffectiveFrom,
      childSiblings,
    ] = await Promise.all([
      childSvc.get(`/${IDChild}`),
      getChildLevelsByChildId(IDChild),
      childSvc
        .post(CHILD_P3.getChildActivityLogsByFilter, {
          child_ids: [IDChild],
        })
        .then(retrieveListOrObject)
        .catch(() => []),

      getChildClosestLevelByChildIds([IDChild]),
      childSvc
        .post(CHILD_P3.getWithdrawlsByFilter, {
          child_ids: [IDChild],
        })
        .then(retrieveListOrObject)
        .catch(() => []),
      childSvc
        .get(CHILD.GET_CHILD_RELATIONS_BY_ID(IDChild))
        .then(retrieveListOrObject)
        .catch(() => []),
      getLastChildLevelByChildId(IDChild),
      childSvc
        .post(...CHILD.GET_CHILD_CURRENT_CLASS_BY_IDS([IDChild]))
        .then(r => retrieveListOrObject(r, false))
        .catch(() => ({})),
      getUpcomingClassByChildIds([IDChild]),
      getBankAccountsByChildIds([IDChild]),
      getTradeDebtNotificationConfigByChildId(IDChild),
      getTemporaryWithdrawalByChildId(IDChild),
      getWithdrawalDraftItemsByChildId(IDChild),
      childSvc
        .get(CHILD_P3.getChildCurrentEstimatedPromotionDate(IDChild))
        .then(r => get(r, 'data.time', null))
        .catch(() => null),
      childSvc
        .get(CHILD_P3.getChildEnrolmentDate(IDChild))
        .then(r => get(r, 'data.enrolmentDate', null))
        .catch(() => null),
      childSvc
        .get(CHILD_P3.getChildProgramTypeUpdateEffectiveFrom(IDChild))
        .then(r => get(r, 'data.effectiveFrom', null))
        .catch(() => null),
      getChildEffectiveDateByCategory(IDChild, 'nationality'),
      getChildEffectiveDateByCategory(IDChild, 'support_schema'),
      childSvc
        .get(CHILD_P3.getChildSiblingsByChildId(IDChild))
        .then(r => get(r, 'data', {}))
        .catch(() => ({})),
    ]);

    const enroledChildrenData = get(childSiblings, 'EnrolledChildren', {});
    const registrationChildren =
      get(childSiblings, 'RegistrationChildren.data') || [];

    const registrationChildIds = uniq(
      compact(registrationChildren?.map(rc => rc?.id))
    );

    const childIds = uniq(
      compact([
        IDChild,
        ...(get(enroledChildrenData, 'data') || [])?.map(ec => ec?.id),
      ])
    );

    const classIds = uniq(
      compact([get(upcomingClasses, `${IDChild}.fk_class`)])
    );

    const [
      childCurrentLevelAndCentre,
      upcomingChildLevels,
      enrolmentStatuses,
      classes,
      ecdaRegistrations,
    ] = await Promise.all([
      getChildCurrentLevelAndCentreByChildIds(childIds),
      getUpcomingLevelByChildIds(childIds),
      getChildEnrolmentStatusByChildIds(childIds),
      getClassByIds(classIds),
      getECDARegistrationsByRegChildIds(registrationChildIds),
    ]);

    const forms = get(upcomingChildLevels, `${IDChild}.id`)
      ? await getFormsByFilter({
          fk_child_level: [
            {
              eq: get(upcomingChildLevels, `${IDChild}.id`),
            },
          ],
        })
      : [];

    const formIds = uniq(compact(forms?.map(f => f?.id)));
    const formParticipants = await getFormParticipantsByFormIds(formIds);

    const levelIds = uniq(
      compact([
        ...childLevels?.map(cl => cl?.fk_level),
        ...closestLevels?.map(cl => cl?.fk_level),
        get(upcomingChildLevels, `${IDChild}.fk_level`),
        get(currentClasses, `${IDChild}.fk_level`),
        ...classes?.map(cl => cl?.fk_level),
        ...Object.values(childCurrentLevelAndCentre).map(ccl =>
          get(ccl, 'child_level.fk_level')
        ),
      ])
    );
    const centreIds = uniq(
      compact([
        ...childLevels?.map(cl => cl?.fk_centre),
        ...closestLevels?.map(cl => cl?.fk_centre),
        ...withdrawls?.map(w => w?.fk_centre),
        get(lastChildLevel, 'fk_centre'),
        get(upcomingChildLevels, `${IDChild}.fk_centre`),
        get(currentClasses, `${IDChild}.fk_centre`),
        ...classes?.map(cl => cl?.fk_centre),
        ...Object.values(ecdaRegistrations)
          .flat()
          .map(er => er?.fk_centre),
      ])
    );
    const programIds = uniq(
      compact([
        ...closestLevels?.map(cl => cl?.fk_program),
        ...Object.values(childCurrentLevelAndCentre).map(ccl =>
          get(ccl, 'child_level.fk_program')
        ),
        get(upcomingChildLevels, `${IDChild}.fk_program`),
        ...Object.values(ecdaRegistrations)
          .flat()
          .map(er => er?.fk_program),
      ])
    );

    const parentIds = uniq(
      compact([
        ...childRelations?.map(fm => fm?.fk_parent),
        ...Object.values(formParticipants)
          .map(fp => fp?.data || [])
          .flat()
          ?.map(f => f?.fk_parent),
      ])
    );

    const userIds = uniq(
      compact([
        ...childRelations?.map(fm => fm?.fk_user),
        ...Object.values(formParticipants)
          .map(fp => fp?.data || [])
          .flat()
          ?.map(f => f?.fk_user),
      ])
    );

    const [
      centres,
      levels,
      programs,
      nextLevels,
      parents,
      users,
      addresses,
      userRoles,
    ] = await Promise.all([
      getCentreByIds(centreIds),
      getLevelByIds(levelIds),
      getProgramByIds(programIds),
      getNextLevelsByLevelIds(levelIds),
      getParentByIds(parentIds),
      getUserByIds(userIds),
      getAddressesByUserIds(userIds),
      getRolesByUserIds(userIds),
    ]);

    return transformChildByID({
      data,
      childLevels,
      levels,
      centres,
      activityLogs,
      enrolmentStatuses,
      closestLevels,
      IDChild,
      programs,
      nextLevels,
      withdrawls,
      childRelations,
      users,
      parents,
      addresses,
      childCurrentLevelAndCentre,
      lastChildLevel,
      upcomingChildLevels,
      currentClasses,
      upcomingClasses,
      classes,
      bankAccounts,
      tradeDebtNotificationConfig,
      forms,
      formParticipants,
      userRoles,
      temporaryWithdrawals,
      withdrawalDraftItems,
      nationalityUpdateEffectiveFrom,
      supportSchemaUpdateEffectiveFrom,
      childCurrentEstimatedPromotionDate,
      enrolmentDate,
      programTypeUpdateEffectiveFrom,
      childSiblings,
      ecdaRegistrations,
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchChildDetails',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msSubmitTemporaryWithdrawalRequest = async (_, reqData) => {
  try {
    const typeMap = {
      // type definition in backend
      create: 1,
      edit: 2,
      cancel: 3,
    };
    const reqBody = get(reqData, 'tempWithdrawalRequest', {});
    const resp = await childService().post(
      CHILD_P3.submitTemporaryWithdrawalRequest,
      {
        effective_from: get(reqBody, 'effectiveFrom'),
        effective_to: get(reqBody, 'effectiveTo'),
        fk_centre: get(reqBody, 'fkCentre'),
        fk_child: get(reqBody, 'fkChild'),
        fk_temporary_withdrawal: get(reqBody, 'fkTemporaryWithdrawal') || null,
        reason_for_change: get(reqBody, 'reasonForChange', ''),
        remark: get(reqBody, 'remark', ''),
        submission_date: get(reqBody, 'submissionDate'),
        type: typeMap[get(reqBody, 'type')] || 1,
        withdrawal_other_reason: get(reqBody, 'withdrawalOtherReason', ''),
        withdrawal_reason: get(reqBody, 'withdrawalReason'),
      }
    );
    return {
      data: {
        submitTemporaryWithdrawalRequest:
          resp?.data &&
          convertObjToCamelCase(
            resp?.data,
            [],
            ['ID:id', 'draftStatus:draft_status_string']
          ),
      },
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'mssubmitTemporaryWithdrawalRequest',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: {
        graphQLErrors: [
          {
            message: get(
              ex,
              'response.data.error',
              get(ex, 'message', DEFAULT_ERROR)
            ),
            errCode: get(ex, 'response.data.errCode', get(ex, 'code')),
            extensions: {
              code: get(ex, 'response.data.errCode', get(ex, 'code')),
            },
          },
        ],
      },
    };
  }
};

export const getTransferDraftsPendingApprovals = async ({
  centreIDs,
  pagination,
}) => {
  try {
    const resp = await childService().post(
      CHILD_P3.getTransferDraftsPendingApprovals,
      {
        centre_ids: centreIDs,
      },
      {
        params: {
          page: pagination?.page || 1,
          perPage: pagination?.perPage || DEFAULT_ITEMS_PER_PAGE,
          sort: pagination?.sort.toString(),
        },
      }
    );

    const destinationCentreIds = get(resp, 'data.data', []).map(transfer =>
      get(transfer, 'destination_fk_centre')
    );
    const childIds = get(resp, 'data.data', []).map(transfer =>
      get(transfer, 'fk_child')
    );

    const [centres, transferDraftItems, childList] = await Promise.all([
      getCentreByIds(destinationCentreIds),
      getTransferDraftItems({ childIds }),
      getChildByIds(childIds),
    ]);

    const centreByIdMap = keyBy(centres.data, 'id');
    const transferDraftItemsByChildMap = keyBy(transferDraftItems, 'fk_child');
    const childById = keyBy(childList, 'id');

    return {
      getTransferDraftsPendingApproval: {
        totalCount: get(resp, 'data.totalCount', 0),
        data: get(resp, 'data.data', []).map(transfer => ({
          ...convertObjToCamelCase(transfer, [
            'additionalStatus:additional_status_string',
            'status:status_string',
            'bankAccountStatus:bank_account_status_string',
          ]),
          centreByDestinationFkCentre: {
            ID: get(transfer, 'destination_fk_centre'),
            label: get(
              centreByIdMap,
              `${get(transfer, 'destination_fk_centre')}.label`
            ),
          },
          transferDraftItem: {
            ...convertObjToCamelCase(
              transferDraftItemsByChildMap[get(transfer, 'fk_child')]
            ),
            child: {
              ID: get(transfer, 'fk_child'),
              fullname: get(
                childById,
                `${get(transfer, 'fk_child')}.fullname`,
                ''
              ),
            },
          },
        })),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getTransferDraftsPendingApprovals',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const getWithdrawalDrafts = async ({
  centreIDs,
  IDCentres,
  pagination,
  filter,
  effectiveFrom,
  effectiveTo,
  fkChild,
} = {}) => {
  try {
    const normalizeFilterValues = value => {
      if (value === undefined || value === null) {
        return null;
      }
      if (Array.isArray(value)) {
        return value.filter(Boolean);
      }
      if (typeof value === 'string' && !value.trim()) {
        return null;
      }
      return [value];
    };

    const statusFilters = normalizeFilterValues(get(filter, 'status')) || [
      'pending',
    ];
    const additionalStatusFilters = normalizeFilterValues(
      get(filter, 'additionalStatus')
    );

    const requestBody = {
      fk_centres: centreIDs || IDCentres || [],
      fk_child: fkChild,
      status: statusFilters,
    };

    if (additionalStatusFilters?.length) {
      requestBody.additional_status = additionalStatusFilters;
    }

    if (effectiveFrom) {
      requestBody.effective_from =
        moment(effectiveFrom).format(SAVE_DATE_FORMAT);
    }

    if (effectiveTo) {
      requestBody.effective_to = moment(effectiveTo).format(SAVE_DATE_FORMAT);
    }
    const sort = pagination?.sort?.length ? pagination?.sort.join(',') : null;
    const resp = await childService().post(
      CHILD_P3.getWithdrawalDrafts,
      requestBody,
      {
        params: {
          page: pagination?.page || DEFAULT_PAGE,
          perPage: pagination?.perPage || DEFAULT_ITEMS_PER_PAGE,
          sort,
        },
      }
    );

    const centreIds = get(resp, 'data.data', []).map(transfer =>
      get(transfer, 'fk_centre')
    );
    const childIds = get(resp, 'data.data', []).map(transfer =>
      get(transfer, 'fk_child')
    );
    const draftIds = get(resp, 'data.data', []).map(transfer =>
      get(transfer, 'id')
    );

    const [
      centres,
      childList,
      closestLevels,
      ennrolmentStatuses,
      withdrawalDraftItems,
    ] = await Promise.all([
      getCentreByIds(centreIds),
      getChildByIds(childIds),
      getClosestLevelByChildIds(childIds),
      getChildEnrolmentStatusByChildIds(childIds),
      getWithdrawalDraftItems({ draftIds }),
    ]);

    const programIds = uniq(
      compact([...closestLevels?.map(cl => cl?.fk_program)])
    );
    const programs = await getProgramByIds(programIds);
    const centreByIdMap = keyBy(centres.data, 'id');
    const childById = keyBy(childList, 'id');
    const levelByChildId = keyBy(closestLevels, 'fk_child');
    const programById = keyBy(programs, 'id');
    const withdrawalDraftItemsByIds = keyBy(withdrawalDraftItems, 'fk_draft');

    return {
      getAllWithdrawalDrafts: {
        totalCount: get(resp, 'data.totalCount', 0),
        data: get(resp, 'data.data', []).map(transfer => ({
          ...convertObjToCamelCase(transfer, [
            'eConsentStatus:e_consent_status_string',
            'status:status_string',
            'additionalStatus:additional_status_string',
          ]),
          centre: {
            ID: get(transfer, 'fk_centre'),
            label: get(centreByIdMap, `${get(transfer, 'fk_centre')}.label`),
          },
          child: {
            ID: get(transfer, 'fk_child'),
            fullname: get(
              childById,
              `${get(transfer, 'fk_child')}.fullname`,
              ''
            ),
            enrolmentStatus: retrieveDataMapper(
              ennrolmentStatuses,
              get(transfer, 'fk_child'),
              ['status', 'label']
            ),
            closestLevel: {
              level: retrieveObjectMapper(
                retrieveDataMapper(levelByChildId, get(transfer, 'fk_child'), [
                  'level',
                ])?.level,
                ['label']
              ),
              program: {
                ...retrieveDataMapper(
                  programById,
                  get(levelByChildId, `${get(transfer, 'fk_child')}`)
                    ?.fk_program,
                  ['label']
                ),
              },
            },
          },
          withdrawalDraftItem: {
            ...convertObjToCamelCase(
              withdrawalDraftItemsByIds[get(transfer, 'id')]
            ),
          },
        })),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getWithdrawalDrafts',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const getPendingForm2ESignatureChildren = async ({
  centreIDs,
  pagination,
  pendingStatus = 'PendingStaffSign',
} = {}) => {
  try {
    const requestBody = {
      centre_ids: centreIDs || [],
      pending_status: pendingStatus,
    };
    const sort = get(pagination, 'sort', []).join(',');

    const resp = await childService().post(
      CHILD_P3.getPendingForm2ESignatureChildren,
      requestBody,
      {
        params: {
          page: get(pagination, 'page', DEFAULT_PAGE),
          perPage: get(pagination, 'perPage', DEFAULT_ITEMS_PER_PAGE),
          ...(sort && { sort }),
        },
      }
    );

    return {
      pendingPromotionSignedChildren: convertObjToCamelCase(
        get(resp, 'data', {})
      ),
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'getPendingForm2ESignatureChildren',
        fileName: 'src/services/ChildService.js',
      },
      'error'
    );
    return [];
  }
};

export const msCancelRetainedChild = async (_, reqData) => {
  try {
    await childService().put(CHILD_P3.cancelRetainedChild, {
      child_id: get(reqData, 'childID'),
    });
    return {
      success: true,
      data: {
        cancelRetainChild: true,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelRetainedChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const getChildCurrentClass = (childIds = []) => {
  if (childIds.length === 0) {
    return [];
  }
  return childService()
    .post(...CHILD.GET_CHILD_CURRENT_CLASS_BY_IDS(childIds))
    .catch(() => ({}));
};

export const msEstimatedPromotionDate = async (childIds = []) => {
  try {
    const res = await childService().post(
      ...CHILD_P3.getChildEnrollmentDateByChildIds(childIds)
    );
    return res?.data;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msEstimatedPromotionDate',
        fileName,
      },
      'error'
    );
    return {};
  }
};

export const msGetChildLevelAtByChildIds = async (childIds = []) => {
  try {
    const body = {
      childIds,
      atTime: `${moment().format(DATE_PICKER_FORMAT)}T00:00:00Z`,
    };
    const res = await childService().post(
      ...CHILD_P3.getChildLevelsByChildIdsAt(body)
    );
    return res?.data;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetChildLevelAtByChildIds',
        fileName,
      },
      'error'
    );
    return {};
  }
};

export const msGetEnrolmentDate = async (childIds = []) => {
  try {
    const res = await childService().post(
      ...CHILD_P3.getEnrolmentDate(childIds)
    );
    return res?.data;
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetEnrolmentDate',
        fileName,
      },
      'error'
    );
    return {};
  }
};

export const msGetChildRelationsByChildIds = async (childIds = []) => {
  try {
    const res = await childService().post(
      ...CHILD_P3.getChildRelationsByChildIds(childIds)
    );
    if (res?.data) {
      const obj = get(res, 'data.data', {});
      const transformedData = Object.keys(obj).map(key => ({
        child_id: parseInt(key, 10),
        ...obj[key],
      }));
      return transformedData;
    }
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetChildRelationsByChildIds',
        fileName,
      },
      'error'
    );
    return {};
  }
};

export const msFetchAllChild = async (_, reqData) => {
  const { filter } = reqData;
  const body = {
    ...(filter?.ID && {
      child_ids: [...filter?.ID],
    }),
  };
  try {
    // note need to add the child filter
    const resp = await childService().post(CHILD_P3.GET_CHILDS_BY_FILTER, body);
    const childIds = get(resp, 'data.data', []).map(c => c.id);
    const [
      enrollmentStatus,
      currentClass,
      upcomingClass,
      currentLevel,
      upcomingLevel,
      promotionData,
      childData,
    ] = await Promise.all([
      getChildEnrolmentStatusByChildIds(childIds),
      getChildCurrentClass(childIds),
      getUpcomingClassByChildIds(childIds),
      getChildCurrentLevelsByChildIds(childIds),
      getUpcomingLevelByChildIds(childIds),
      msEstimatedPromotionDate(childIds),
      getChildByIds(childIds),
    ]);

    const fkCentres = uniq(
      compact([
        ...Object.values(get(currentClass, 'data.data', [])).map(
          cl => cl?.fk_centre
        ),
        ...Object.values(get(upcomingClass, 'data.data', [])).map(
          cl => cl?.fk_centre
        ),
        ...Object.values(currentLevel).map(cl => cl?.fk_centre),
        ...Object.values(upcomingLevel).map(cl => cl?.fk_centre),
      ])
    );

    const fkLevels = uniq(
      compact([
        ...Object.values(get(currentClass, 'data.data', [])).map(
          cl => cl?.fk_level
        ),
        ...Object.values(get(upcomingClass, 'data.data', [])).map(
          cl => cl?.fk_level
        ),
      ])
    );

    const [levels, centres, nextLevels] = await Promise.all([
      fkLevels?.length > 0 ? getLevelByIds(fkLevels) : [],
      fkCentres?.length > 0 ? getCentreByIds(fkCentres) : {},
      getNextLevelsByLevelIds(fkLevels),
    ]);
    return {
      success: true,
      ...transfromFetchAllChild({
        data: get(resp, 'data.data'),
        enrollmentStatus,
        currentClass,
        upcomingClass,
        currentLevel,
        upcomingLevel,
        levels,
        centres,
        nextLevels,
        promotionData,
        childData,
      }),
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAllChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msApproveTransferRequestDestination = async (_, reqData) => {
  try {
    if (!reqData?.draftId) return { success: true, data: {} };

    const body = {
      ...(reqData?.bypassExceeding && {
        bypass_exceeding: reqData?.bypassExceeding,
      }),
      transfer_draft_id: reqData?.draftId,
      remarks: reqData?.remarks || '',
    };

    const childSvc = childService();
    const resp = await childSvc.post(
      ...CHILD_P3.approveTransferRequestByDestinationCentreStaff(body)
    );

    return {
      success: true,
      data: {
        approveTransferRequestByDestinationCentreStaff:
          resp?.data && convertObjToCamelCase(resp?.data),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msApproveTransferRequestDestination',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const msRejectTransferRequestDestination = async (_, reqData) => {
  try {
    if (!reqData?.draftId) return { success: true, data: {} };

    const body = {
      transfer_draft_id: reqData?.draftId,
      remarks: reqData?.remarks || '',
    };

    const childSvc = childService();
    const resp = await childSvc.post(
      ...CHILD_P3.rejectTransferRequestByDestinationCentreStaff(body)
    );

    return {
      success: true,
      data: {
        rejectTransferRequestByDestinationCentreStaff:
          resp?.data?.success || false,
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msRejectTransferRequestDestination',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const msDownloadMeasurementTemplate = async (_, reqData) => {
  try {
    const resp = await childService().get(
      CHILD_P3.getDownloadMeasurementTemplate(
        get(reqData, 'classID'),
        get(reqData, 'level')
      )
    );
    return {
      data: {
        getMeasurementTemplate: get(resp, 'data.url'),
      },
      loading: false,
      success: true,
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msDownloadMeasurementTemplate',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUploadChildDocsFile = async (_, reqData) => {
  try {
    const body = {
      ...(reqData?.IDChild && {
        child_id: reqData?.IDChild,
      }),
      ...(reqData?.IDDocumentTypeConfig && {
        document_type_config_id: reqData?.IDDocumentTypeConfig,
      }),
      file: {
        file_name: get(reqData, 'file.filename'),
        file_path: get(reqData, 'file.filePath'),
        file_size: get(reqData, 'file.fileSize'),
        mime_type: get(reqData, 'file.MIMEType'),
      },
    };

    const childSvc = childService();
    const resp = await childSvc.post(
      ...CHILD_P3.getDocumentUploadUrlByDocumentTypeConfig(body)
    );

    return {
      success: true,
      data: {
        getDocumentUploadURLByIDDocumentTypeConfig: {
          Url: get(resp, 'data.url', ''),
          Key: get(resp, 'data.key', ''),
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msUploadChildDocsFile',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const msGetChildRelationFromParentId = parentId => {
  if (!parentId) return {};
  return childService()
    .get(CHILD_P3.getParentRelationById(parentId))
    .then(r => retrieveListOrObject(r, false))
    .catch(() => ({}));
};

export const msCancelTransferRequestByUser = async (_, reqData) => {
  try {
    const resp = await childService().post(
      CHILD_P3.cancelTransferRequestByUser,
      {
        transfer_draft_id: get(reqData, 'draftId'),
        remarks: get(reqData, 'remarks'),
        ...(get(reqData, 'bypassExceeding') !== undefined && {
          bypass_exceeding: get(reqData, 'bypassExceeding'),
        }),
      }
    );

    return {
      success: true,
      data: {
        cancelTransferRequestByUser: get(resp, 'data.success', false),
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msCancelTransferRequestByUser',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          extensions: {
            errorCode: get(ex, 'response.data.errCode'),
            ExceedingCapacity: get(ex, 'response.data.ExceedingCapacity') || [],
          },
          message: get(
            ex,
            'response.data.error',
            get(ex, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const getChildCurrentClassesByChildIds = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return {};

    const [currentClass, currentLevelAndCentre] = await Promise.all([
      getChildCurrentClass(childIds),
      getChildCurrentLevelAndCentreByChildIds(childIds),
    ]);

    const currentchildLevel = Object.values(currentLevelAndCentre) || [];
    const levelIds = uniq(
      compact(currentchildLevel?.map(cL => cL?.child_level?.fk_level) || [])
    );
    const levels = await getLevelByIds(levelIds);

    const data = currentClass?.data?.data;
    const combined = Object.keys(data).reduce((result, key) => {
      if (currentLevelAndCentre?.[key]) {
        result[key] = {
          ...data?.[key],
          level:
            levels?.find(
              ({ id }) =>
                id === currentLevelAndCentre?.[key]?.child_level?.fk_level
            ) || null,
          centre: currentLevelAndCentre?.[key]?.centre || null,
        };
      }
      return result;
    }, {});
    return combined;
  } catch (error) {
    return error;
  }
};

export const getChildCurrentLevelByChildIds = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return {};

    const [currentLevels, currentLevelAndCentre] = await Promise.all([
      getChildCurrentLevelsByChildIds(childIds),
      getChildCurrentLevelAndCentreByChildIds(childIds),
    ]);

    const centreAndChildLevels = Object.values(currentLevelAndCentre);
    const programIds = uniq(
      compact(
        centreAndChildLevels?.map(cL => cL?.child_level?.fk_program) || []
      )
    );
    const levelIds = uniq(
      compact(Object.values(currentLevels)?.map(({ id }) => id) || [])
    );

    const [nextLevels, programs] = await Promise.all([
      getNextLevelsByLevelIds(levelIds),
      getProgramByIds(programIds),
    ]);

    const combined =
      Object.keys(currentLevelAndCentre)?.reduce((result, key) => {
        if (currentLevels?.[key]) {
          const currentLevelId = currentLevels?.[key]?.id;
          result[key] = {
            ...currentLevelAndCentre?.[key]?.child_level,
            centre: currentLevelAndCentre?.[key]?.centre || null,
            level: currentLevels?.[key] && {
              ...currentLevels?.[key],
              next_level: nextLevels?.[currentLevelId] || null,
            },
            next_level: nextLevels?.[currentLevelId] || null,
            program:
              programs?.find(
                ({ id }) =>
                  id === currentLevelAndCentre?.[key]?.child_level?.fk_program
              ) || null,
          };
        }
        return result;
      }, {}) || {};
    return combined;
  } catch (error) {
    return error;
  }
};

export const getUpcomingLevelsByChildIds = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return {};

    const upcomingLevels = await getUpcomingLevelByChildIds(childIds);
    const programIds = uniq(
      compact(Object.values(upcomingLevels)?.map(lvl => lvl?.fk_program) || [])
    );
    const levelIds = uniq(
      compact(Object.values(upcomingLevels)?.map(lvl => lvl?.fk_level) || [])
    );
    const fkCentres = uniq(
      compact(Object.values(upcomingLevels)?.map(lvl => lvl?.fk_centre) || [])
    );

    const [levels, nextLevels, programs, centres] = await Promise.all([
      getLevelByIds(levelIds),
      getNextLevelsByLevelIds(levelIds),
      getProgramByIds(programIds),
      getCentreByIds(fkCentres),
    ]);

    const combined =
      Object.keys(upcomingLevels)?.reduce((result, key) => {
        if (upcomingLevels?.[key]) {
          const currentLevel = levels?.find(
            ({ id }) => id === upcomingLevels?.[key].fk_level
          );
          result[key] = {
            ...upcomingLevels?.[key],
            level: {
              ...currentLevel,
              next_level: nextLevels?.[currentLevel?.id] || null,
            },
            centre:
              centres?.data?.find(
                ({ id }) => id === upcomingLevels?.[key].fk_centre
              ) || null,
            program:
              programs?.find(
                ({ id }) => id === upcomingLevels?.[key].fk_program
              ) || null,
            next_level: nextLevels?.[upcomingLevels?.[key].fk_level] || null,
          };
        }
        return result;
      }, {}) || {};
    return combined;
  } catch (error) {
    return error;
  }
};

export const getChildClosestLevels = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return [];

    const closestLevel = await getChildClosestLevelByChildIds(childIds);
    const fkLevels = uniq(
      compact(closestLevel?.map(({ fk_level }) => fk_level) || [])
    );
    const fkCentres = uniq(
      compact(closestLevel?.map(({ fk_centre }) => fk_centre) || [])
    );
    const fkPrograms = uniq(
      compact(closestLevel?.map(({ fk_program }) => fk_program) || [])
    );

    const [levels, nextLevels, programs, centres] = await Promise.all([
      getLevelByIds(fkLevels),
      getNextLevelsByLevelIds(fkLevels),
      getProgramByIds(fkPrograms),
      getCentreByIds(fkCentres),
    ]);

    return (
      closestLevel?.map(cL => ({
        ...cL,
        level: levels?.find(({ id }) => id === cL?.fk_level) || null,
        centre: centres?.data?.find(({ id }) => id === cL?.fk_centre) || null,
        program: programs?.find(({ id }) => id === cL?.fk_program) || null,
        next_level: nextLevels?.[cL?.fk_level] || null,
      })) || []
    );
  } catch (error) {
    return error;
  }
};

export const getChildEnquiresByChildIds = async (childIds = []) => {
  try {
    if (childIds?.length === 0) return {};

    // Need to update with multiple child ids.
    const childEnquires = await getEnquiriesByChildId(childIds);

    return childEnquires;
  } catch (error) {
    return error;
  }
};

export const msFetchChildrenData = async (_, { reqData = {} }) => {
  try {
    const { childFilter } = reqData;
    const body = {
      child_filter: childFilter && {
        ...(childFilter?.firstname && {
          firstname: childFilter?.firstname,
        }),
        ...(childFilter?.fullname && {
          fullname: childFilter?.fullname,
        }),
        ...(childFilter?.lastname && {
          lastname: childFilter?.lastname,
        }),
        ...(childFilter?.ID && {
          id: childFilter?.ID,
        }),
      },
      ...(reqData?.childStatus && {
        child_status: reqData?.childStatus,
      }),
      ...(reqData?.ftsInput && {
        fts_input: reqData?.ftsInput,
      }),
    };

    const childSvc = childService();
    const resp = await childSvc.post(...CHILD_P3.getChildrenData(body));
    const childIds = uniq(compact(resp?.data?.data?.map(({ id }) => id) || []));

    if (childIds?.length === 0) {
      return {
        success: true,
        data: {
          getChildrenData: {
            totalCount: 0,
            data: [],
          },
        },
      };
    }

    const [
      enrolmentStatus,
      enquiries,
      currentClasses,
      upcomingClasses,
      currentLevels,
      upcomingLevels,
      closestLevels,
    ] = await Promise.all([
      getChildEnrolmentStatusByChildIds(childIds),
      getChildEnquiresByChildIds(childIds),
      getChildCurrentClassesByChildIds(childIds),
      getUpcomingClassesByChildIds(childIds),
      getChildCurrentLevelByChildIds(childIds),
      getUpcomingLevelsByChildIds(childIds),
      getChildClosestLevels(childIds),
    ]);

    return {
      success: true,
      data: {
        getChildrenData: {
          data: transformChildrenData({
            children: resp?.data?.data,
            enrolmentStatus,
            currentClasses,
            upcomingClasses,
            currentLevels,
            upcomingLevels,
            closestLevels,
            enquiries,
          }),
          totalCount: resp?.data?.totalCount || 0,
        },
      },
    };
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchChildrenData',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const msGetAllWithdrawalDrafts = async (_, reqData) => {
  try {
    const resp = await getWithdrawalDrafts({
      IDCentres: reqData?.IDCentres,
      centreIDs: reqData?.centreIDs,
      pagination: reqData?.pagination,
      filter: reqData?.filter,
      effectiveFrom: reqData?.effectiveFrom,
      effectiveTo: reqData?.effectiveTo,
      fkChild: reqData?.fkChild,
    });
    if (resp?.success === false) {
      return resp;
    }
    return {
      success: true,
      data: resp,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetAllWithdrawalDrafts',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            error?.response?.data?.error || error?.message || DEFAULT_ERROR,
        },
      ],
    };
  }
};

export const msUpdateChildImage = async (_, reqBody) => {
  try {
    const resp = await childService().put(
      ...CHILD_P3.updateChildByFields(get(reqBody, 'IDChild'), {
        image_key: get(reqBody, 'data.imageKey'),
      })
    );
    return {
      success: true,
      data: {
        updateChildByFields: retrieveObjectMapper(resp?.data, [
          'ID:id',
          'imageKeyPresignedURL:image_key_presigned_url',
        ]),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateChildByFields',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msFetchClassHealthDetails = async (_, reqData) => {
  try {
    const resp = await childService().post(
      CHILD_P3.getMeasurementsByForChild,
      {
        centre_id: get(reqData, 'centreId'),
        class_id: get(reqData, 'classId'),
        date: get(reqData, 'date'),
      },
      {
        params: {
          page: get(reqData, 'pagination.page', DEFAULT_PAGE),
          perPage: get(reqData, 'pagination.perPage', DEFAULT_ITEMS_PER_PAGE),
        },
      }
    );

    return {
      success: true,
      data: {
        getAttendanceByClass: {
          total: get(resp, 'data.totalCount', 0),
          attendances: (get(resp, 'data.data', []) || []).map(item => ({
            child: convertObjToCamelCase(item?.child),
            measurements: (get(item, 'child_measurements') || []).map(msm =>
              convertObjToCamelCase(msm)
            ),
          })),
        },
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetMeasurementsForChild',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: {
        message: get(
          error,
          'response.data.error',
          get(error, 'message', DEFAULT_ERROR)
        ),
      },
    };
  }
};

export const msPromoteChildrenAndAssignClass = async payload => {
  try {
    const { childrenDetail } = payload;
    const res = await childService().post(
      CHILD_P3.promoteChildrenAndAssignClasses,
      {
        child_promotion_dto: childrenDetail.map(child => ({
          child_id: child?.IDChild,
          class_id: child?.IDClass,
          effective_date: child?.effectiveDate,
        })),
      }
    );
    return {
      success: res?.data?.success,
      data: {
        promoteChildrenAndAssignClass: res?.data?.success,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msPromoteChildrenAndAssignClass',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};

export const msCancelPromotion = async (_, reqData) => {
  try {
    const { childID, bypassExceeding } = reqData;
    const res = await childService().post(
      ...CHILD_P3.cancelPromotion({
        child_id: childID,
        bypass_exceeding: bypassExceeding,
      })
    );
    return {
      success: res?.data?.success || true,
      data: {
        cancelPromotion: res?.data?.success || true,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelPromotion',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [{ message: get(error, 'response.data.error', DEFAULT_ERROR) }],
    };
  }
};

export const msGetUpcomingClassByChildIds = (childIds = []) => {
  if (!childIds?.length) return {};
  return childService()
    .get(CHILD_P3.getUpcomingClassByChildIds(childIds))
    .then(r => r.data)
    .catch(() => ({}));
};

export const msFetchAssignChildrenToClass = async (_, reqData = {}) => {
  try {
    const { childFilter } = reqData;
    const params = {
      page: get(reqData, 'pagination.page', DEFAULT_PAGE),
      perPage: get(reqData, 'pagination.perPage', DEFAULT_ITEMS_PER_PAGE),
    };
    const body = {
      centre_ids: get(reqData, 'centreIDs', []),
      child_filter: {
        ...(get(childFilter, 'firstEnrolmentDate') && {
          first_enrolment_date: get(childFilter, 'firstEnrolmentDate'),
        }),
      },
      ...(get(reqData, 'classIDs.length', 0) > 0 && {
        class_ids: get(reqData, 'classIDs'),
      }),
      ...(get(reqData, 'dobFrom') && {
        dob_from: get(reqData, 'dobFrom'),
      }),
      ...(get(reqData, 'dobTo') && {
        dob_to: get(reqData, 'dobTo'),
      }),
      ...(get(reqData, 'hhiRange') && {
        hhi_range: get(reqData, 'hhiRange'),
      }),
      ...(get(reqData, 'levelIDs.length', 0) > 0 && {
        level_ids: get(reqData, 'levelIDs'),
      }),
      ...(get(reqData, 'programIDs.length', 0) > 0 && {
        program_ids: get(reqData, 'programIDs'),
      }),
      ...(get(reqData, 'promotionDateFrom') && {
        promotion_date_from: get(reqData, 'promotionDateFrom'),
      }),
      ...(get(reqData, 'promotionDateTo') && {
        promotion_date_to: get(reqData, 'promotionDateTo'),
      }),
      ...(get(reqData, 'pendingPromotion') && {
        pending_promotion: get(reqData, 'pendingPromotion'),
      }),
      ...(get(reqData, 'retainedPromotion') && {
        retained_promotion: get(reqData, 'retainedPromotion'),
      }),
      ...(get(reqData, 'promotedChild') && {
        promoted_child: get(reqData, 'promotedChild'),
      }),
      ...(get(reqData, 'statuses.length') && {
        status: get(reqData, 'statuses'),
      }),
    };
    const childSvc = childService();
    const resp = await childSvc.post(
      ...CHILD_P3.getChildrenToAssignToClass(body, params)
    );
    const childIds = uniq(compact(resp?.data?.data?.map(({ id }) => id) || []));
    const [
      enrolmentStatusData,
      currentClassData,
      upcomingClassData,
      currentLevelData,
      upcomingLevelData,
      closestLevelData,
      childData,
      childCurrentEstimatedPromotionDateData,
      enrolmentDateData,
      childRelationsData,
      childLevelData,
    ] = await Promise.all([
      getChildEnrolmentStatusByChildIds(childIds),
      getChildCurrentClass(childIds),
      msGetUpcomingClassByChildIds(childIds),
      getChildCurrentLevelsByChildIds(childIds),
      getUpcomingLevelByChildIds(childIds),
      getChildClosestLevelByChildIds(childIds),
      getChildByIds(childIds),
      msEstimatedPromotionDate(childIds),
      msGetEnrolmentDate(childIds),
      msGetChildRelationsByChildIds(childIds),
      msGetChildLevelAtByChildIds(childIds),
    ]);
    const fkParents = uniq(
      compact(
        childRelationsData.flatMap(item =>
          get(item, 'data', []).map(d => d.fk_parent)
        )
      )
    );
    const currentClassValues = Object.values(
      get(currentClassData, 'data.data', [])
    );
    const upcomingClassValues = Object.values(upcomingClassData);
    const currentLevelValues = Object.values(currentLevelData);
    const upcomingLevelValues = Object.values(upcomingLevelData);
    // /child-class/upcoming-by-child-ids?child_ids= can we have fk_centre and fk_level
    const fkCentres = uniq(
      compact([
        ...currentClassValues.map(v => get(v, 'fk_centre')),
        ...upcomingClassValues.map(v => get(v, 'fk_centre')),
        ...currentLevelValues.map(v => get(v, 'fk_centre')),
        ...upcomingLevelValues.map(v => get(v, 'fk_centre')),
        ...closestLevelData.map(v => get(v, 'fk_centre')),
      ])
    );
    const fkLevels = uniq(
      compact([
        ...currentClassValues.map(v => get(v, 'fk_level')),
        ...upcomingClassValues.map(v => get(v, 'fk_level')),
        ...currentLevelValues.map(v => get(v, 'id')),
        ...upcomingLevelValues.map(v => get(v, 'id')),
        ...closestLevelData.map(v => get(v, 'fk_level')),
      ])
    );
    const fkPrograms = uniq(
      compact([
        ...closestLevelData.map(v => get(v, 'fk_program')),
        ...get(childLevelData, 'data', []).map(v => get(v, 'fk_program')),
        ...upcomingLevelValues.map(v => get(v, 'fk_program')),
        ...currentLevelValues.map(v => get(v, 'fk_program')),
      ])
    );
    const [parentData, centreData, levelData, nextLevelData, programData] =
      await Promise.all([
        getParentByIds(fkParents),
        getCentreByIds(fkCentres),
        getLevelByIds(fkLevels),
        getNextLevelsByLevelIds(fkLevels),
        getProgramByIds(fkPrograms),
      ]);
    return msTransformChildAssignToClassData({
      resp,
      enrolmentStatusData,
      currentClasses: get(currentClassData, 'data.data', []),
      upcomingClassData,
      currentLevelData,
      upcomingLevelData,
      closestLevelData,
      childData,
      childCurrentEstimatedPromotionDateData,
      enrolmentDateData,
      childRelationsData,
      parentData,
      childLevelData,
      centreData,
      levelData,
      nextLevelData,
      programData,
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'msFetchAssignChildrenToClass',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(ex, 'response.data.error', get(ex, 'message', DEFAULT_ERROR)),
    };
  }
};

export const msNewTransferRequest = async reqData => {
  try {
    const resp = await childService().post(CHILD_P3.newTransferRequest, {
      level_id: reqData?.IDLevel,
      child_id: reqData?.fkChild,
      to_centre: reqData?.toCentre,
      remarks: reqData?.remarks,
      notification_date: reqData?.notificationDate,
      effective_from: reqData?.effectiveFrom,
      to_centre_fee_tier_id: reqData?.toCentreFeeTierID,
      reason: reqData?.reason,
    });

    return {
      success: true,
      data: {
        submitNewTransferRequest: convertObjToCamelCase(get(resp, 'data', {})),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msNewTransferRequest',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message: get(
            error,
            'response.data.error',
            get(error, 'message', DEFAULT_ERROR)
          ),
        },
      ],
    };
  }
};
